import { Injectable } from '@angular/core';
import { StoredData } from '@vending/sync-engine-client/lib/models/storedData';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { ArticleModel } from 'src/app/models/articles';
import { ErrorService } from 'src/packages/mitsBasics';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { ArticleService } from 'src/app/providers/model-services/article.service';
import { BasketModel, BufferstockModel } from 'src/packages/warehouse/models';
import { StockPositionModel } from 'src/packages/warehouse/models/position';

@Injectable()
export class ArticleLoadHelper {
  constructor(
    private readonly articleService: ArticleService,
    private readonly errorService: ErrorService
  ) {}

  //////////////////////////////////////////////////////////////
  //// Öffentliche Methoden

  /**
   * Lädt alle Artikel aus den übergebenen Positionen
   * - Akkumuliert dabei alle eindeutigen Artikel-IDs aus allen Positionen
   * - Akzeptiert jede Positions-Art, die eine article_id trägt
   *   (Stock-/Auftrags-/Disposal-Positionen, Fahrzeuglager-Positionen)
   * @param positions die Positionen, deren Artikel geladen werden sollen
   * @returns alle Artikel aus den Positionen
   */
  public async loadSourceArticles(
    positions: ReadonlyArray<{ article_id?: number }>
  ): Promise<ArticleModel[]> {
    const articleIds: Set<number> = new Set<number>();

    positions.forEach((position): void => {
      if (position.article_id !== undefined) {
        articleIds.add(position.article_id);
      }
    });

    return await this.loadArticles(Array.from(articleIds));
  }

  /**
   * Baut aus den übergebenen Positionen eine Map article_id -> Bestand (amount).
   * Wird der mits-article-list als Anzeige- und Begrenzungs-Bestand übergeben.
   * @param positions Positionen mit Artikel-ID und Bestand
   * @returns Map von Artikel-ID auf Bestand
   */
  public stockAmountsFromPositions(
    positions: ReadonlyArray<{ article_id?: number; amount?: number }>,
  ): Record<number, number> {
    const amounts: Record<number, number> = {};
    for (const position of positions ?? []) {
      if (position.article_id !== undefined) {
        amounts[position.article_id] = position.amount ?? 0;
      }
    }
    return amounts;
  }

  /**
   * Lädt alle Artikel aus dem Quellbestand, um sie den verfügbaren Artikeln zuzuordnen
   * - Akkumuliert dabei alle eindeutigen Artikel-IDs aus allen Baskets
   * @param sourceStock der Quell-Bufferstock, dessen Artikel geladen werden sollen
   * @returns alle Artikel aus den Baskets
   */
  public async loadSourceBasketsArticles(
    sourceStock: BufferstockModel
  ): Promise<ArticleModel[]> {
    const articleIds: Set<number> = new Set<number>();

    sourceStock.baskets.forEach((basket: BasketModel): void => {
      basket.positions.forEach((position: StockPositionModel) =>
        articleIds.add(position.article_id)
      );
    });

    return await this.loadArticles(Array.from(articleIds));
  }

  //////////////////////////////////////////////////////////////
  //// Private Hilfsmethoden

  /**
   * Lädt die Artikel mit den übergebenen IDs
   * - Lädt die Artikel mit localBulkFind()
   * @param articleIds - die IDs der Artikel, die geladen werden sollen
   * @private
   * @returns die geladenen Artikel
   */
  private async loadArticles(articleIds: number[]): Promise<ArticleModel[]> {
    if (!articleIds || articleIds.length === 0) return [];
    return this.articleService
      .localBulkFind(articleIds)
      .then((results: StoredData<ArticleModel>[]) => {
        return (
          results
            ?.map((result: StoredData<ArticleModel>) => result?.content)
            // Filtert alle undefinierten Werte aus
            .filter((content) => content !== undefined)
        );
      })
      .catch((error) => {
        this.errorService.handle(error);
        return [];
      });
  }
}
